import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { useUser } from '@/context/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Users, TrendingUp, Search, Plus, Download, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_COLORS = {
  'A appeler': 'bg-gray-50 text-gray-600 border-gray-200',
  'A rappeler': 'bg-gray-50 text-gray-600 border-gray-200',
  'RDV pris': 'bg-gray-50 text-gray-600 border-gray-200',
  'RDV realise': 'bg-gray-50 text-gray-600 border-gray-200',
};
const ETAT_COLORS = {
  'A renseigner': 'bg-gray-50 text-gray-600 border-gray-200',
  'Propale en negociation': 'bg-gray-50 text-gray-600 border-gray-200',
  'Perdu': 'bg-red-50 text-red-700 border-red-200',
  'Concu': 'bg-red-50 text-red-700 border-red-200',
  'RAF': 'bg-gray-50 text-gray-600 border-gray-200',
  'Client RH': 'bg-green-50 text-green-700 border-green-200',
  'Client FIPRO': 'bg-green-50 text-green-700 border-green-200',
  'Client PMS': 'bg-green-50 text-green-700 border-green-200',
  'A renouveler': 'bg-gray-50 text-gray-600 border-gray-200',
};

const ActionBadge = ({ value }) => {
  const v = value || 'A appeler';
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap ${ACTION_COLORS[v] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{v}</span>;
};
const EtatBadge = ({ value }) => {
  const v = value || 'A renseigner';
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap ${ETAT_COLORS[v] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{v}</span>;
};

export { ACTION_COLORS, ETAT_COLORS, ActionBadge, EtatBadge };

const LODEOM_COLORS = {
  'Innovation et croissance': 'bg-violet-50 text-violet-700 border-violet-200',
  'Competitivite renforcee': 'bg-sky-50 text-sky-700 border-sky-200',
  'Competitivite': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'HL': 'bg-gray-50 text-gray-400 border-gray-200',
};
const LodeomBadge = ({ value, alt }) => {
  const v = value || 'HL';
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap ${LODEOM_COLORS[v] || LODEOM_COLORS['HL']}`}>{v}{alt ? '*' : ''}</span>;
};
export { LODEOM_COLORS, LodeomBadge };

const Dashboard = () => {
  const { canEdit, isDirector, isAdmin } = useUser();
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterNaf, setFilterNaf] = useState('all');
  const [filterLodeom, setFilterLodeom] = useState('all');
  const [filterEffectif, setFilterEffectif] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [filterEtat, setFilterEtat] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [sortColumn, setSortColumn] = useState('sector');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [manualSize, setManualSize] = useState('');
  const [formData, setFormData] = useState({
    siren: '',
    name: '',
    size: '',
    sector: '',
    location: '',
    address: '',
    postal_code: '',
    city: '',
    region: 'Guadeloupe',
    legal_status: '',
    creation_date: '',
  });
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [companiesRes, statsRes] = await Promise.all([
        axios.get(`${API}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setCompanies(companiesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const nafCodes = useMemo(() => {
    const codes = new Set();
    companies.forEach(c => {
      if (c.sector) codes.add(c.sector);
    });
    return [...codes].sort();
  }, [companies]);

  const effectifTranches = useMemo(() => {
    const tranches = new Set();
    companies.forEach(c => {
      if (c.tranche_effectif) tranches.add(c.tranche_effectif);
    });
    return [...tranches].sort((a, b) => parseInt(a) - parseInt(b));
  }, [companies]);

  const actionOptions = Object.keys(ACTION_COLORS);
  const etatOptions = Object.keys(ETAT_COLORS);

  const tagOptions = useMemo(() => {
    const tags = new Set();
    companies.forEach(c => (c.tags || []).forEach(t => tags.add(t)));
    return [...tags].sort();
  }, [companies]);

  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = companies;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name?.toLowerCase().includes(term) ||
          c.siren?.includes(searchTerm) ||
          c.city?.toLowerCase().includes(term) ||
          c.sector?.toLowerCase().includes(term)
      );
    }

    if (filterRegion !== 'all') {
      filtered = filtered.filter((c) => c.region === filterRegion);
    }

    if (filterNaf !== 'all') {
      filtered = filtered.filter((c) => c.sector === filterNaf);
    }

    if (filterLodeom !== 'all') {
      filtered = filtered.filter((c) => (c.bareme_lodeom || 'HL') === filterLodeom);
    }

    if (filterEffectif !== 'all') {
      filtered = filtered.filter((c) => c.tranche_effectif === filterEffectif);
    }

    if (filterAction !== 'all') {
      filtered = filtered.filter((c) => (c.action || 'A appeler') === filterAction);
    }

    if (filterEtat !== 'all') {
      filtered = filtered.filter((c) => (c.etat_compte || 'A renseigner') === filterEtat);
    }

    if (filterTag !== 'all') {
      filtered = filtered.filter((c) => (c.tags || []).includes(filterTag));
    }

    // Sorting
    const sorted = [...filtered].sort((a, b) => {
      let valA, valB;
      if (sortColumn === 'sector') {
        valA = (a.sector || '').toLowerCase();
        valB = (b.sector || '').toLowerCase();
      } else if (sortColumn === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (sortColumn === 'size') {
        valA = a.size || 0;
        valB = b.size || 0;
      } else if (sortColumn === 'region') {
        valA = (a.region || '').toLowerCase();
        valB = (b.region || '').toLowerCase();
      } else if (sortColumn === 'city') {
        valA = (a.city || '').toLowerCase();
        valB = (b.city || '').toLowerCase();
      } else {
        valA = (a[sortColumn] || '').toString().toLowerCase();
        valB = (b[sortColumn] || '').toString().toLowerCase();
      }

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [companies, searchTerm, filterRegion, filterNaf, filterLodeom, filterEffectif, filterAction, filterEtat, filterTag, sortColumn, sortDirection]);

  const toggleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
      : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };

  const handleLookup = async () => {
    if (!lookupQuery || lookupQuery.length < 2) return;
    setIsSearching(true);
    setLookupResults([]);
    setSelectedCompany(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/companies/lookup?q=${encodeURIComponent(lookupQuery)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLookupResults(res.data);
      if (res.data.length === 0) toast.info('Aucune entreprise trouvee');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la recherche');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectResult = (result) => {
    if (!result.is_allowed_region) {
      toast.error(`Entreprise hors campagne (dept. ${result.department}). Seules les entreprises en Guadeloupe, Martinique, Guyane, Saint-Martin et Saint-Barthelemy peuvent etre ajoutees.`);
      return;
    }
    setSelectedCompany(result);
    setManualSize(result.size ? String(result.size) : '');
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    const size = parseInt(manualSize);
    if (!size || size <= 0) {
      toast.error('Veuillez indiquer un effectif valide');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/companies`, {
        siren: selectedCompany.siren,
        name: selectedCompany.name,
        size: size,
        tranche_effectif: selectedCompany.tranche_effectif || null,
        sector: selectedCompany.sector || '',
        address: selectedCompany.address || '',
        postal_code: selectedCompany.postal_code || '',
        city: selectedCompany.city || '',
        region: selectedCompany.region,
        legal_status: selectedCompany.legal_status || '',
        creation_date: selectedCompany.creation_date || '',
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Entreprise ajoutee avec succes');
      setIsDialogOpen(false);
      setLookupQuery('');
      setLookupResults([]);
      setSelectedCompany(null);
      setManualSize('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const handleAddCompanyManual = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/companies`, { ...formData, size: parseInt(formData.size) }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Entreprise ajoutee avec succes');
      setIsDialogOpen(false);
      fetchData();
      setFormData({ siren: '', name: '', size: '', sector: '', location: '', address: '', postal_code: '', city: '', region: 'Guadeloupe', legal_status: '', creation_date: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const resetDialog = () => {
    setLookupQuery('');
    setLookupResults([]);
    setSelectedCompany(null);
    setManualSize('');
  };

  const handleImportInsee = async () => {
    setIsImporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/insee/enriched-search`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${response.data.message} - ${response.data.companies_imported} entreprises et ${response.data.contacts_imported} contacts ajoutés !`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'importation');
    } finally {
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'export_crm.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Export réussi !');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const regions = ['Guadeloupe', 'Martinique', 'Guyane', 'Saint-Martin', 'Saint-Barthélemy'];

  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          <div className="col-span-full md:col-span-3 bg-white rounded-xl border border-border/50 shadow-sm p-6" data-testid="total-companies-stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-accent rounded-lg">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">Entreprises</p>
            </div>
            <p className="text-3xl font-heading font-bold text-gray-900">{stats?.total_companies || 0}</p>
          </div>

          <div className="col-span-full md:col-span-3 bg-white rounded-xl border border-border/50 shadow-sm p-6" data-testid="total-contacts-stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-accent rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">Contacts</p>
            </div>
            <p className="text-3xl font-heading font-bold text-gray-900">{stats?.total_contacts || 0}</p>
          </div>

          <div className="col-span-full md:col-span-3 bg-white rounded-xl border border-border/50 shadow-sm p-6" data-testid="growth-companies-stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-secondary/20 rounded-lg">
                <TrendingUp className="h-5 w-5 text-secondary" />
              </div>
              <p className="text-sm text-muted-foreground">Hyper Croissance</p>
            </div>
            <p className="text-3xl font-heading font-bold text-gray-900">{stats?.companies_with_growth || 0}</p>
          </div>

          <div className="col-span-full md:col-span-3 flex flex-col gap-2">
            {isDirector && (
            <Button
              onClick={handleImportInsee}
              disabled={isImporting}
              className="flex-1 bg-[#1A4D2E] text-white hover:bg-[#153d24] rounded-full shadow-lg shadow-[#1A4D2E]/20 transition-all active:scale-95 gap-2"
              data-testid="import-insee-btn"
            >
              <RefreshCw className={`h-4 w-4 ${isImporting ? 'animate-spin' : ''}`} />
              {isImporting ? 'Import...' : 'Import INSEE'}
            </Button>
            )}
            <Button
              onClick={handleExport}
              variant="outline"
              className="flex-1 rounded-full gap-2"
              data-testid="export-excel-btn"
            >
              <Download className="h-4 w-4" />
              Export Excel
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border/50 shadow-sm p-6 mb-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1 w-full md:w-auto relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, SIREN, ville, secteur NAF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>

              {isAdmin && (
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetDialog(); }}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#1A4D2E] text-white hover:bg-[#153d24] rounded-full shadow-lg shadow-[#1A4D2E]/20 transition-all active:scale-95 gap-2 w-full md:w-auto"
                    data-testid="add-company-btn"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Ajouter une entreprise</DialogTitle>
                  </DialogHeader>

                  {!selectedCompany ? (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">Recherchez par nom ou numero SIREN dans la base officielle INSEE.</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Nom de l'entreprise ou SIREN..."
                          value={lookupQuery}
                          onChange={(e) => setLookupQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookup(); } }}
                          data-testid="lookup-input"
                        />
                        <Button onClick={handleLookup} disabled={isSearching || lookupQuery.length < 2} className="bg-[#1A4D2E] text-white hover:bg-[#153d24] gap-2" data-testid="lookup-btn">
                          {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                          Rechercher
                        </Button>
                      </div>

                      {lookupResults.length > 0 && (
                        <div className="space-y-2 max-h-[350px] overflow-y-auto" data-testid="lookup-results">
                          {lookupResults.map((r, i) => (
                            <div
                              key={i}
                              onClick={() => handleSelectResult(r)}
                              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                r.is_allowed_region
                                  ? 'border-border hover:border-blue-400 hover:bg-blue-50/50'
                                  : 'border-red-200 bg-red-50/50 opacity-70'
                              }`}
                              data-testid="lookup-result-item"
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-semibold text-sm">{r.name}</p>
                                  <p className="text-xs text-muted-foreground">SIREN: {r.siren} {r.city && `- ${r.city}`} {r.postal_code && `(${r.postal_code})`}</p>
                                  {r.sector && <p className="text-xs text-muted-foreground">NAF: {r.sector}</p>}
                                </div>
                                <div className="text-right">
                                  {r.is_allowed_region ? (
                                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">{r.region}</span>
                                  ) : (
                                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Hors campagne
                                    </span>
                                  )}
                                  {r.size ? (
                                    <p className="text-xs text-muted-foreground mt-1">{r.size} employes</p>
                                  ) : (
                                    <p className="text-xs text-orange-500 mt-1">Effectif inconnu</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleAddCompany} className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="selected-company-preview">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="h-4 w-4 text-blue-600" />
                          <span className="font-semibold text-sm">Entreprise selectionnee</span>
                        </div>
                        <p className="font-bold">{selectedCompany.name}</p>
                        <p className="text-sm text-muted-foreground">SIREN: {selectedCompany.siren} - {selectedCompany.city} ({selectedCompany.region})</p>
                        {selectedCompany.sector && <p className="text-sm text-muted-foreground">NAF: {selectedCompany.sector}</p>}
                        {selectedCompany.legal_status && <p className="text-sm text-muted-foreground">Statut: {selectedCompany.legal_status}</p>}
                        {selectedCompany.dirigeants && selectedCompany.dirigeants.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-muted-foreground">Dirigeants :</p>
                            {selectedCompany.dirigeants.map((d, i) => (
                              <p key={i} className="text-xs text-muted-foreground">- {d.name} ({d.qualite})</p>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="effectif">Effectif {!selectedCompany.size && <span className="text-red-500">* (non disponible - saisie obligatoire)</span>}</Label>
                        <Input
                          id="effectif"
                          type="number"
                          min="1"
                          value={manualSize}
                          onChange={(e) => setManualSize(e.target.value)}
                          required
                          placeholder="Nombre d'employes"
                          data-testid="manual-size-input"
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={() => setSelectedCompany(null)} className="flex-1 rounded-full">
                          Retour
                        </Button>
                        <Button type="submit" className="flex-1 bg-[#1A4D2E] text-white hover:bg-[#153d24] rounded-full" data-testid="confirm-add-btn">
                          Ajouter l'entreprise
                        </Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Select value={filterRegion} onValueChange={setFilterRegion}>
                <SelectTrigger className="w-[160px]" data-testid="region-filter">
                  <SelectValue placeholder="Toutes regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes regions</SelectItem>
                  {regions.map((region) => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterNaf} onValueChange={setFilterNaf}>
                <SelectTrigger className="w-[150px]" data-testid="naf-filter">
                  <SelectValue placeholder="Tous NAF" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous NAF</SelectItem>
                  {nafCodes.map((code) => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterLodeom} onValueChange={setFilterLodeom}>
                <SelectTrigger className="w-[200px]" data-testid="lodeom-filter">
                  <SelectValue placeholder="Tous LODEOM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous LODEOM</SelectItem>
                  <SelectItem value="Innovation et croissance">Innovation et croissance</SelectItem>
                  <SelectItem value="Competitivite renforcee">Competitivite renforcee</SelectItem>
                  <SelectItem value="Competitivite">Competitivite</SelectItem>
                  <SelectItem value="HL">HL (Hors LODEOM)</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterEffectif} onValueChange={setFilterEffectif}>
                <SelectTrigger className="w-[160px]" data-testid="effectif-filter">
                  <SelectValue placeholder="Tous effectifs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous effectifs</SelectItem>
                  {effectifTranches.map((t) => (
                    <SelectItem key={t} value={t}>Tranche {t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="w-[160px]" data-testid="action-filter">
                  <SelectValue placeholder="Toutes actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes actions</SelectItem>
                  {actionOptions.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterEtat} onValueChange={setFilterEtat}>
                <SelectTrigger className="w-[180px]" data-testid="etat-filter">
                  <SelectValue placeholder="Tous etats" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous etats</SelectItem>
                  {etatOptions.map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {tagOptions.length > 0 && (
                <Select value={filterTag} onValueChange={setFilterTag}>
                  <SelectTrigger className="w-[160px]" data-testid="tag-filter">
                    <SelectValue placeholder="Tous tags" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous tags</SelectItem>
                    {tagOptions.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>

        {/* Tableau des entreprises */}
        <div className="bg-white rounded-xl border border-border/50 shadow-sm overflow-hidden" data-testid="companies-list">
          <div className="px-4 py-2 bg-accent/10 border-b border-border/50 text-xs text-muted-foreground">
            {filteredAndSortedCompanies.length} entreprise{filteredAndSortedCompanies.length > 1 ? 's' : ''} affichee{filteredAndSortedCompanies.length > 1 ? 's' : ''}
          </div>
          {filteredAndSortedCompanies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Aucune entreprise trouvee.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-accent/30 border-b border-border">
                  <tr>
                    <th
                      className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50 transition-colors"
                      onClick={() => toggleSort('name')}
                    >
                      <span className="inline-flex items-center">Entreprise <SortIcon column="name" /></span>
                    </th>
                    <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700">SIREN</th>
                    <th
                      className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50 transition-colors"
                      onClick={() => toggleSort('sector')}
                    >
                      <span className="inline-flex items-center">NAF <SortIcon column="sector" /></span>
                    </th>
                    <th
                      className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50 transition-colors"
                      onClick={() => toggleSort('size')}
                    >
                      <span className="inline-flex items-center">Eff. <SortIcon column="size" /></span>
                    </th>
                    <th
                      className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50 transition-colors"
                      onClick={() => toggleSort('region')}
                    >
                      <span className="inline-flex items-center">Region <SortIcon column="region" /></span>
                    </th>
                    <th
                      className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50 transition-colors"
                      onClick={() => toggleSort('action')}
                    >
                      <span className="inline-flex items-center">Action <SortIcon column="action" /></span>
                    </th>
                    <th
                      className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50 transition-colors"
                      onClick={() => toggleSort('etat_compte')}
                    >
                      <span className="inline-flex items-center">Etat <SortIcon column="etat_compte" /></span>
                    </th>
                    <th
                      className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50 transition-colors"
                      onClick={() => toggleSort('bareme_lodeom')}
                    >
                      <span className="inline-flex items-center">LODEOM <SortIcon column="bareme_lodeom" /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedCompanies.map((company) => (
                    <tr
                      key={company.id}
                      className="border-b border-border/50 hover:bg-accent/20 transition-colors group"
                      data-testid="company-row"
                    >
                      <td className="px-3 py-2">
                        <Link
                          to={`/companies/${company.id}`}
                          className="flex items-center gap-2 no-underline"
                          data-testid="company-link"
                        >
                          <div className="p-1.5 bg-accent rounded-lg">
                            <Building2 className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span className="font-semibold text-gray-900 text-sm group-hover:text-primary transition-colors">{company.name}</span>
                          {company.situation_juridique && company.situation_juridique !== 'Actif' && (
                            <span className="text-red-500 text-xs font-bold ml-0.5" title={`${company.situation_juridique}${company.situation_details ? ' - ' + company.situation_details : ''}`} data-testid="situation-indicator">*</span>
                          )}
                          {company.added_by && (
                            <span className="text-[9px] text-muted-foreground italic" data-testid="added-by-label">({company.added_by})</span>
                          )}
                          {company.tags && company.tags.length > 0 && company.tags.map(tag => (
                            <span key={tag} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-200" data-testid="company-tag">{tag}</span>
                          ))}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{company.siren}</td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                          {company.sector || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {company.size}
                        {company.tranche_effectif && 
                          <span className="text-[10px] text-muted-foreground ml-0.5">(T{company.tranche_effectif})</span>
                        }
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-medium">
                          {company.region}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <ActionBadge value={company.action} />
                      </td>
                      <td className="px-3 py-2">
                        <EtatBadge value={company.etat_compte} />
                      </td>
                      <td className="px-3 py-2">
                        <LodeomBadge value={company.bareme_lodeom} alt={company.bareme_lodeom_alt} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;